import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Send, Download, Copy, Pencil, Check, Eye, X, Play, CheckCheck } from 'lucide-react';
import { useAgreement, useAgreements } from '@/composables/financial/useAgreements';
import { agreementService } from '@/services/agreement.service';
import { usePdfActions } from '@/composables/shared/usePdfActions';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { StatusBadge } from '@/components/StatusBadge';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { formatDate, formatCurrency } from '@/lib/formatters';
import type { PaymentMilestoneValue } from '@/types/table';

const EDITABLE = new Set(['DRAFT', 'SENT']);
const TERMINAL = new Set(['COMPLETED', 'CANCELLED']);

export function AgreementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { agreement, isLoading } = useAgreement(id!);
  const { sendAgreement, markSigned, markActive, markCompleted, cancelAgreement, duplicateAgreement } = useAgreements();
  const [confirmCancel, setConfirmCancel] = useState(false);

  const pdf = usePdfActions();
  const handleDownload = () => pdf.download(() => agreementService.downloadPdf(id!), `agreement-${agreement?.agreementNumber || id}.pdf`);
  const handleViewPdf = () => pdf.view(() => agreementService.downloadPdf(id!));

  if (isLoading) return <LoadingSpinner />;
  if (!agreement) return <div className="p-8 text-center">Agreement not found</div>;

  const canEdit = EDITABLE.has(agreement.status);
  const isTerminal = TERMINAL.has(agreement.status);
  const milestones = (agreement.variableValues?.PAYMENT_MILESTONES as PaymentMilestoneValue[] | undefined) || [];
  const scope = (agreement.variableValues?.PROJECT_SCOPE_LIST as string[] | undefined) || [];

  return (
    <PageContainer backTo
      title={`${agreement.kind} ${agreement.agreementNumber}`}
      actions={
        <>
          <Button variant="outline" onClick={handleViewPdf} loading={pdf.isViewing}><Eye className="mr-2 h-4 w-4" />View PDF</Button>
          <Button variant="outline" onClick={handleDownload} loading={pdf.isDownloading}><Download className="mr-2 h-4 w-4" />Download</Button>
          {canEdit && <Button variant="outline" onClick={() => navigate(`/financial/agreements/${agreement.id}/edit`)}><Pencil className="mr-2 h-4 w-4" />Edit</Button>}
          {agreement.status === 'DRAFT' && <Button onClick={() => sendAgreement.mutate(agreement.id)}><Send className="mr-2 h-4 w-4" />Send</Button>}
          {(agreement.status === 'DRAFT' || agreement.status === 'SENT') && <Button variant="outline" onClick={() => markSigned.mutate(agreement.id)}><Check className="mr-2 h-4 w-4" />Mark Signed</Button>}
          {agreement.status === 'SIGNED' && <Button onClick={() => markActive.mutate(agreement.id)}><Play className="mr-2 h-4 w-4" />Mark Active</Button>}
          {(agreement.status === 'SIGNED' || agreement.status === 'ACTIVE') && <Button onClick={() => markCompleted.mutate(agreement.id)}><CheckCheck className="mr-2 h-4 w-4" />Mark Completed</Button>}
          <Button variant="outline" onClick={() => duplicateAgreement.mutate(agreement.id)}><Copy className="mr-2 h-4 w-4" />Duplicate</Button>
          {!isTerminal && <Button variant="outline" onClick={() => setConfirmCancel(true)}><X className="mr-2 h-4 w-4" />Cancel</Button>}
        </>
      }
    >
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle className="text-base">Info</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Row label="Title"><span className="font-medium">{agreement.title}</span></Row>
            <Row label="Type"><Badge variant="outline">{agreement.kind}</Badge></Row>
            <Row label="Status"><StatusBadge status={agreement.status} type="agreement" /></Row>
            <Row label="Date"><span>{formatDate(agreement.agreementDate)}</span></Row>
            {agreement.totalAmount != null && (
              <Row label="Value"><span className="font-bold">{formatCurrency(Number(agreement.totalAmount), agreement.currency)}</span></Row>
            )}
            <Row label="Client">
              <div className="text-right">
                {agreement.clientCompanyName && <div className="font-medium">{agreement.clientCompanyName}</div>}
                <div className={agreement.clientCompanyName ? 'text-xs text-muted-foreground' : ''}>{agreement.clientRepName || '-'}</div>
              </div>
            </Row>
            {agreement.invoice && (
              <Row label="Invoice">
                <Button variant="link" size="sm" className="h-auto p-0" onClick={() => navigate(`/financial/invoices/${agreement.invoice!.id}`)}>{agreement.invoice.invoiceNumber} →</Button>
              </Row>
            )}
            {agreement.signature && (
              <Row label="Signature"><span className="text-xs">{agreement.signature.label}</span></Row>
            )}
            <Row label="Created"><span className="text-muted-foreground text-xs">{formatDate(agreement.createdAt)}</span></Row>
            {agreement.sentAt && <Row label="Sent"><span className="text-muted-foreground text-xs">{formatDate(agreement.sentAt)}</span></Row>}
            {agreement.signedAt && <Row label="Signed"><span className="text-muted-foreground text-xs">{formatDate(agreement.signedAt)}</span></Row>}
            {agreement.completedAt && <Row label="Completed"><span className="text-muted-foreground text-xs">{formatDate(agreement.completedAt)}</span></Row>}
            {agreement.cancelledAt && <Row label="Cancelled"><span className="text-muted-foreground text-xs">{formatDate(agreement.cancelledAt)}</span></Row>}
          </CardContent>
        </Card>

        <div className="lg:col-span-2 space-y-6">
          {scope.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Project Scope</CardTitle></CardHeader>
              <CardContent>
                <ul className="list-disc pl-5 text-sm space-y-1">{scope.map((s, i) => <li key={i}>{s}</li>)}</ul>
              </CardContent>
            </Card>
          )}

          {milestones.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Payment Milestones</CardTitle></CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground border-b">
                    <tr><th className="text-left py-2">Milestone</th><th className="text-right">Percent</th><th className="text-right">Amount</th><th className="text-right">Due</th></tr>
                  </thead>
                  <tbody>
                    {milestones.map((m, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-2">{m.name}</td>
                        <td className="text-right">{m.percent}%</td>
                        <td className="text-right">{agreement.totalAmount != null ? formatCurrency((Number(agreement.totalAmount) * (Number(m.percent) || 0)) / 100, agreement.currency) : '-'}</td>
                        <td className="text-right text-muted-foreground">{m.dueDate ? formatDate(m.dueDate) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {agreement.notes && (
            <Card>
              <CardHeader><CardTitle className="text-base">Internal Notes</CardTitle></CardHeader>
              <CardContent className="text-sm whitespace-pre-wrap">{agreement.notes}</CardContent>
            </Card>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmCancel}
        onOpenChange={setConfirmCancel}
        title="Cancel Agreement?"
        description="This marks the agreement as CANCELLED. You won't be able to send or sign it after this."
        variant="destructive"
        confirmLabel="Cancel Agreement"
        onConfirm={() => { setConfirmCancel(false); cancelAgreement.mutate(agreement.id); }}
        isLoading={cancelAgreement.isPending}
      />
    </PageContainer>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <div className="text-right">{children}</div>
    </div>
  );
}
